import React from 'react';
import { StaticPage, TopbarContainer } from '..';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { propTypes } from '../../util/types';
import { bool, number } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  FAQ,
  IconSuccess,
  NamedLink,
} from '../../components';
import { createSlug } from '../../util/urlHelpers';

import css from './TrailerShieldPage.module.css';
import trailerShieldLogo from '../../assets/trailer-shield-logo.png';

const TrailerShieldPage = props => {
  const {
    isAuthenticated,
    currentUserPublishedListingsCount,
    currentUserFirstPublishedListing = null,
    currentUser = null,
  } = props;

  // Determine button text and link based on user state
  const getButtonConfig = () => {
    if (!isAuthenticated || currentUserPublishedListingsCount === 0) {
      return {
        text: 'TrailerShieldPage.createListing',
        linkName: 'NewListingPage',
        linkParams: {},
      };
    } else if (currentUserPublishedListingsCount === 1 && currentUserFirstPublishedListing) {
      return {
        text: 'TrailerShieldPage.upgradeListingToPremium',
        linkName: 'EditListingPage',
        linkParams: {
          id: currentUserFirstPublishedListing.id.uuid,
          slug: createSlug(currentUserFirstPublishedListing.attributes.title),
          type: 'edit',
          tab: 'subscription',
        },
      };
    } else {
      return {
        text: 'TrailerShieldPage.upgradeListingsToPremium',
        linkName: 'ManageListingsPage',
        linkParams: {},
      };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <StaticPage
      title="Trailer Shield for Owners"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description:
          'Trailer Shield gives owners peace of mind by protecting trailers from hirer damage — included with Premium listings.',
        name: 'Trailer Shield - Owners',
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.staticPageWrapper}>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <div className={css.titleContainer}>
                  <h1 className={css.sectionTitle}>Protect Your Trailer with Trailer Shield</h1>
                  <img
                    src={trailerShieldLogo}
                    className={css.trailerShieldLogo}
                    alt="Trailer Shield Logo"
                  />
                </div>
                <div className={css.columns}>
                  <div className={css.sectionContentPitch}>
                    <p>
                      Whether you've got a trailer listed or you're just getting started, Trailer
                      Shield has your back. It's included with every Premium listing, helping you
                      stand out, build trust with hirers, and lock in more bookings — all with
                      built-in protection for you and your trailer.
                    </p>
                  </div>
                  <div>
                    <h2 className={css.sectionSubtitle}>Why It Matters</h2>
                    <div className={css.glanceSection}>
                      <div className={css.glanceRow}>
                        <IconSuccess fillColor={'var(--marketplaceColor)'} />
                        <span>Reduce risk of out-of-pocket repair costs</span>
                      </div>
                      <div className={css.glanceRow}>
                        <IconSuccess fillColor={'var(--marketplaceColor)'} />
                        <span>Give customers peace of mind when booking</span>
                      </div>
                      <div className={css.glanceRow}>
                        <IconSuccess fillColor={'var(--marketplaceColor)'} />
                        <span>Help secure more bookings with built-in protection</span>
                      </div>
                      <div className={css.glanceRow}>
                        <IconSuccess fillColor={'var(--marketplaceColor)'} />
                        <span>Streamlined support if something goes wrong</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>Included with Premium Listings</h1>
                <p>
                  When you upgrade to Premium, every hire of your trailer includes Trailer Shield
                  damage protection. That means faster resolution in the event of any damage and
                  greater peace of mind for you and your customers.
                </p>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h1 className={css.sectionTitle}>FAQs</h1>
                <div className={css.faqSection}>
                  <FAQ category={'trailerShieldOwner'} />
                </div>
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <div className={css.readySection}>
                  <div className={css.readySectionText}>
                    <h1 className={css.sectionTitle}>Upgrade to Premium</h1>
                    <p>
                      Trailer Shield is automatically included in Premium listings — unlock
                      protection today.
                    </p>
                    {isAuthenticated && currentUserPublishedListingsCount > 0 ? (
                      <p>
                        {currentUserPublishedListingsCount === 1
                          ? 'Upgrade your listing to Premium to get Trailer Shield protection.'
                          : 'Head to "My Listings" and click on "Upgrade to Premium" to upgrade your listings to Premium.'}
                      </p>
                    ) : (
                      <p>Create a listing and select the 'Premium' listing type.</p>
                    )}
                  </div>
                  <div className={css.readySectionButton}>
                    <NamedLink
                      className={css.premiumListingButton}
                      name={buttonConfig.linkName}
                      params={buttonConfig.linkParams}
                    >
                      <FormattedMessage id={buttonConfig.text} />
                    </NamedLink>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

TrailerShieldPage.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserPublishedListingsCount: number.isRequired,
  currentUser: propTypes.currentUser,
  currentUserFirstPublishedListing: propTypes.ownListing,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const { currentUserPublishedListingsCount, currentUserFirstPublishedListing } = state.user;
  return {
    isAuthenticated,
    currentUserPublishedListingsCount,
    currentUserFirstPublishedListing,
  };
};

export default compose(connect(mapStateToProps))(TrailerShieldPage);
